import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { CHAT_MESSAGE_TYPES } from 'cpool-constants';
import { AGGREGATE_NAMES, CHAT_COMMANDS } from 'cpool-constants';
import { useTranslation } from 'react-i18next';

import { APICommunicator } from '~/api/APICommunicator';
import { toastChatCommandError } from '~/features/whatsapp/utils/toastChatCommandError';
import { queryKeys } from '~/utils/queryKeys';

type SendMessagePayload =
    | { messageType: typeof CHAT_MESSAGE_TYPES.TEXT; text: string }
    | {
          messageType: typeof CHAT_MESSAGE_TYPES.MEDIA;
          mediaKey: string;
          mimeType: string;
          caption?: string;
          mediaFileName?: string;
      };

// Backend fail-closes outside the 24h window — Composer prevents this from being called in that state.
export const useSendMessageMutation = (chatId: string) => {
    const queryClient = useQueryClient();
    const { t } = useTranslation();

    return useMutation({
        mutationFn: (payload: SendMessagePayload) =>
            APICommunicator.sendCommand({
                aggregateId: chatId,
                aggregateName: AGGREGATE_NAMES.CHAT,
                commandName: CHAT_COMMANDS.sendMessage,
                payload,
            }),
        onError: (error) => {
            toastChatCommandError(error, t, 'WHATSAPP_CHAT.ERROR.SEND_FAILED');
        },
        onSuccess: () => {
            // Skip useChainAwareInvalidation — the chain includes manta's Meta round-trip, which would delay the queued bubble.
            queryClient.invalidateQueries({ queryKey: queryKeys.getChatMessages(chatId) });
            queryClient.invalidateQueries({ queryKey: queryKeys.getChats() });
        },
    });
};
